import { memo, type ReactNode } from "react"
import { formatDate, formatCurrency } from "@shared/utils/formatters"
import { formatDateOnly } from '@shared/utils/date'
import { TruncatedCell } from "./TruncatedCell"
import { cn } from "@shared/lib/utils"

interface IdCellProps {
  id: string | number
}

export const IdCell = memo(({ id }: IdCellProps) => (
  <span className="font-mono text-sm">{id.toString()}</span>
))
IdCell.displayName = 'IdCell'

interface TextCellProps {
  value?: string | null
  maxWidth?: number
  placeholder?: string
  className?: string
}

export const TextCell = memo(({
  value,
  maxWidth = 160,
  placeholder = "—",
  className
}: TextCellProps) => {
  const displayValue = value || placeholder
  return (
    <TruncatedCell
      maxWidth={maxWidth}
      className={cn("text-muted-foreground font-normal", className)}
    >
      {displayValue}
    </TruncatedCell>
  )
})
TextCell.displayName = 'TextCell'

interface DynamicDateCellProps {
  timestamp?: string | number | Date | null
  className?: string
  placeholder?: ReactNode
  showTime?: boolean
}

/**
 * Safely converts various timestamp formats to a Date object
 * Handles string, number, or Date inputs with proper error handling
 * @returns Date object or null if conversion fails
 */
const parseTimestamp = (timestamp: string | number | Date): Date | null => {
  try {
    if (timestamp instanceof Date) return timestamp
    const date = new Date(timestamp)
    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

export const DynamicDateCell = memo(({
  timestamp,
  className,
  placeholder = "—",
  showTime = true
}: DynamicDateCellProps) => {
  if (!timestamp) {
    return <span className="text-muted-foreground">{placeholder}</span>
  }
  
  const date = parseTimestamp(timestamp)
  if (!date) {
    return <span className="text-muted-foreground">Invalid Date</span>
  }
  
  const formattedDate = showTime ? formatDate(date) : formatDateOnly(date)
  
  return (
    <span
      className={cn("text-muted-foreground font-normal", className)}
      title={formatDate(date)}
    >
      {formattedDate}
    </span>
  )
})
DynamicDateCell.displayName = 'DynamicDateCell'

interface CurrencyCellProps {
  amount: number
  currency?: string
  decimals?: number
  className?: string
  align?: "left" | "right"
}

export const CurrencyCell = memo(({
  amount,
  currency = 'USD',
  decimals,
  className = "",
  align = "left"
}: CurrencyCellProps) => {
  const formattedAmount = formatCurrency(amount, currency, decimals)
  const [amountPart, currencyPart] = formattedAmount.split(' ')
  
  return (
    <div className={cn(
      "flex items-baseline gap-1",
      align === "right" ? "justify-end" : "justify-start",
      className
    )}>
      <span className="text-sm font-medium text-foreground">
        {amountPart}
      </span>
      {currencyPart && (
        <span className="text-xs text-muted-foreground">
          {currencyPart}
        </span>
      )}
    </div>
  )
})
CurrencyCell.displayName = 'CurrencyCell'

interface IconTextCellProps {
  icon: ReactNode
  children: ReactNode
  className?: string
  iconClassName?: string
}

export const IconTextCell = memo(({
  icon,
  children,
  className = "",
  iconClassName = ""
}: IconTextCellProps) => (
  <div className={cn("flex items-center gap-1.5", className)}>
    <span className={iconClassName}>{icon}</span>
    {children}
  </div>
))
IconTextCell.displayName = 'IconTextCell'
